// VenueMixChart — the cross-venue differentiator: a daily venue-segmented
// stacked bar of options flow, with a Notional / Premium / Trades toggle. The
// Premium flip is the edge over notional-only views (participant quality, not
// just size). Days with incomplete coverage (`partial`) are HATCHED per venue,
// never rendered as a false low.
//
// Responsive SVG: width="100%" with PERCENTAGE x/width (crisp — no viewBox
// stretch) for the columns, pixel y for the stack. Only rects live in the SVG;
// the measure toggle, date axis, and legend are HTML aligned to the columns.

import { useMemo, useState } from 'react';
import { Box, Stack } from '@mui/material';

import { useThemeMode } from '../../../../providers/theme-provider';
import { getTokenValue, tokens } from '../../../../styles/tokens';
import {
  COMPARISON_MEASURES,
  ComparisonMeasure,
  fmtCount,
  fmtUsd,
  orderVenues,
  venueColorToken,
} from '../../../../providers/options-flow/options-flow.helpers';
import { VenueComparisonPoint } from '../../../../providers/options-flow/types';

const CHART_H = 190;
const TOP_PAD = 6;
const BOTTOM_PAD = 2;
const MONO = 'ui-monospace, Menlo, Consolas, monospace';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Axis/tooltip label for a bucket. Daily/weekly buckets are a bare date
// ("23 Jul"); hourly buckets (the 24h window) label by hour ("14:00") — the
// point's `date` carries a time component in that case.
const fmtBucket = (iso: string, granularity?: string): string => {
  const hasTime = /\d{2}:\d{2}/.test(iso);
  const d = new Date(hasTime ? iso : `${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  if (granularity === 'hourly') {
    return `${String(d.getUTCHours()).padStart(2, '0')}:00`;
  }
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
};

const Toggle = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <Box
    role="tab"
    aria-selected={active}
    onClick={onClick}
    sx={{
      cursor: 'pointer',
      fontFamily: MONO,
      fontSize: 10,
      fontWeight: 700,
      px: '10px',
      py: '3px',
      borderRadius: '5px',
      color: active ? tokens.flow.accent : tokens.text.muted,
      backgroundColor: active ? tokens.bg.widget : 'transparent',
      border: `1px solid ${active ? tokens.flow.accent : tokens.border.default}`,
      '&:hover': { backgroundColor: tokens.bg.widget },
    }}
  >
    {children}
  </Box>
);

// Series/measures are the ACTIVE WINDOW's slice (resolved by the panel from the
// global activeWindow), so the chart is window-agnostic. `granularity` labels
// the axis (daily / weekly for longer windows).
interface VenueMixChartProps {
  series: VenueComparisonPoint[];
  measures: string[];
  granularity?: string;
}

export const VenueMixChart = ({
  series,
  measures: measureKeys,
  granularity,
}: VenueMixChartProps) => {
  const { resolvedMode } = useThemeMode();

  // Measures offered = the report's list intersected with the ones we render;
  // default to the first (notional).
  const measures = useMemo(
    () => COMPARISON_MEASURES.filter((m) => (measureKeys ?? []).includes(m.key)),
    [measureKeys],
  );
  const measureList = measures.length > 0 ? measures : COMPARISON_MEASURES;
  const [measure, setMeasure] = useState<ComparisonMeasure>(measureList[0].key);
  const active = measureList.find((m) => m.key === measure) ?? measureList[0];

  // Venues present anywhere in the series, in canonical stacking order.
  const venues = useMemo(
    () => orderVenues([...new Set(series.flatMap((p) => Object.keys(p.venues ?? {})))]),
    [series],
  );

  const venueHex = useMemo(() => {
    const map = new Map<string, string>();
    venues.forEach((v) => map.set(v, getTokenValue(venueColorToken(v), resolvedMode)));
    return map;
  }, [venues, resolvedMode]);

  const hatch = getTokenValue('text.muted', resolvedMode);

  const val = (v: number | undefined) => (Number.isFinite(v) ? (v as number) : 0);

  // Per-day totals (for the shared y-scale) — over whatever venues reported.
  const totals = series.map((p) =>
    venues.reduce((sum, v) => sum + val(p.venues?.[v]?.[active.field] as number | undefined), 0),
  );
  const maxTotal = Math.max(1, ...totals);

  const n = series.length;
  const colStep = n > 0 ? 100 / n : 100;
  const colWidth = colStep * 0.62; // % of chart width
  const available = CHART_H - TOP_PAD - BOTTOM_PAD;
  const baseY = CHART_H - BOTTOM_PAD;

  const anyPartial = series.some((p) => (p.partial?.length ?? 0) > 0);
  const fmt = (x: number) => (active.usd ? fmtUsd(x) : fmtCount(x));

  return (
    <Box>
      <Stack
        direction="row"
        spacing={0.75}
        role="tablist"
        alignItems="center"
        sx={{ mb: 1, flexWrap: 'wrap' }}
      >
        {measureList.map((m) => (
          <Toggle key={m.key} active={m.key === measure} onClick={() => setMeasure(m.key)}>
            {m.label}
          </Toggle>
        ))}
        {granularity && (
          <Box sx={{ fontSize: 9.5, color: tokens.text.muted, ml: 0.5 }}>{granularity} buckets</Box>
        )}
      </Stack>

      <Box sx={{ position: 'relative', width: '100%' }} data-testid="venue-mix-svg-wrap">
        <svg
          width="100%"
          height={CHART_H}
          style={{ display: 'block' }}
          role="presentation"
          preserveAspectRatio="none"
        >
          <defs>
            <pattern
              id="vc-hatch"
              width={4}
              height={4}
              patternUnits="userSpaceOnUse"
              patternTransform="rotate(45)"
            >
              <line x1={0} y1={0} x2={0} y2={4} stroke={hatch} strokeWidth={1} opacity={0.55} />
            </pattern>
          </defs>
          {series.map((p, i) => {
            const left = i * colStep + (colStep - colWidth) / 2;
            // partial[] mixes two cases: a venue ABSENT from `venues{}` is simply
            // not drawn (a GAP); a venue PRESENT in `venues{}` but in partial[]
            // (a present-but-incomplete t-0 venue) is drawn HATCHED/lighter so
            // it reads as provisional rather than a true value.
            const partialSet = new Set(p.partial ?? []);
            let cum = 0;
            return venues.map((v) => {
              const raw = val(p.venues?.[v]?.[active.field] as number | undefined);
              if (raw <= 0) return null; // absent venue -> gap
              const isPartial = partialSet.has(v); // present-but-incomplete -> hatched
              const segH = (raw / maxTotal) * available;
              const y = baseY - cum - segH;
              cum += segH;
              // y/height via style (CSS geometry props) so the Notional<->
              // Premium flip animates — Deribit's premium slice visibly grows,
              // Bybit's shrinks. x/width stay percentage attributes (responsive).
              const geo = { y, height: segH, transition: 'y 0.35s ease, height 0.35s ease' };
              return (
                <g key={`${p.date}-${v}`}>
                  <rect
                    x={`${left}%`}
                    width={`${colWidth}%`}
                    fill={venueHex.get(v)}
                    opacity={isPartial ? 0.4 : 0.9}
                    style={geo}
                  >
                    <title>{`${v} · ${fmtBucket(p.date, granularity)}: ${fmt(raw)}${isPartial ? ' (partial coverage)' : ''}`}</title>
                  </rect>
                  {isPartial && (
                    <rect x={`${left}%`} width={`${colWidth}%`} fill="url(#vc-hatch)" style={geo} />
                  )}
                </g>
              );
            });
          })}
        </svg>

        {/* Date axis — one cell per column, aligned under the bars. */}
        <Box sx={{ display: 'flex', mt: '4px' }} data-testid="venue-mix-dates">
          {series.map((p) => (
            <Box
              key={p.date}
              sx={{
                flex: `0 0 ${colStep}%`,
                textAlign: 'center',
                fontSize: 9,
                fontFamily: MONO,
                color: tokens.text.muted,
                whiteSpace: 'nowrap',
              }}
            >
              {fmtBucket(p.date, granularity)}
              {(p.partial?.length ?? 0) > 0 ? ' ▨' : ''}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Legend — consistent venue colours; a partial-coverage note when relevant. */}
      <Stack direction="row" spacing={1.5} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
        {venues.map((v) => (
          <Stack key={v} direction="row" alignItems="center" spacing={0.5}>
            <Box
              sx={{ width: 9, height: 9, borderRadius: '2px', backgroundColor: venueHex.get(v) }}
            />
            <Box sx={{ fontSize: 10, color: tokens.text.secondary, textTransform: 'capitalize' }}>
              {v}
            </Box>
          </Stack>
        ))}
        {anyPartial && (
          <Box sx={{ fontSize: 9.5, color: tokens.text.muted }}>▨ partial coverage</Box>
        )}
      </Stack>
    </Box>
  );
};
